import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  CircularProgress,
  alpha,
  MenuItem,
  Grid,
  useTheme,
  InputAdornment,
  Card,
  CardContent,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import PaymentView from './PaymentView';
import {
  Add as AddIcon,
  Payment as PaymentIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpwardIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { checkVouchersHaveTransactions } from '../../../services/financeService';
import { printPayment } from '../../../utils/printVoucher';

// تعريف نوع بيانات سند الصرف
interface Payment {
  id: string;
  paymentNumber: string;
  date: string;
  amount: number;
  debitAccount: { id: string; name: string };
  creditAccount: { id: string; name: string };
  description: string;
  status: string;
  createdBy: string;
  createdAt: string;
  supplier: string;
  branchId?: string;
  branchName?: string;
}

interface VoucherTransactionStatus {
  status: 'complete' | 'partial' | 'missing';
  voucherAmount: number;
  debitAmount: number;
  creditAmount: number;
  transactionsCount: number;
  expectedTransactions: number;
  debitAccount: string;
  creditAccount: string;
  hasDebitTransaction: boolean;
  hasCreditTransaction: boolean;
  issues: string[] | null;
}

// مكون مؤشر حالة المعاملات
interface TransactionStatusIndicatorProps {
  transactionStatus: VoucherTransactionStatus | null;
  loading: boolean;
  voucherNumber: string;
}

const TransactionStatusIndicator: React.FC<TransactionStatusIndicatorProps> = ({
  transactionStatus,
  loading,
  voucherNumber
}) => {
  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (!transactionStatus) {
    return <Chip label="لا توجد بيانات" size="small" color="default" />;
  }

  const getStatusIcon = (): React.ReactElement | undefined => {
    switch (transactionStatus.status) {
      case 'complete':
        return <CheckCircleIcon />;
      case 'partial':
        return <WarningIcon />;
      case 'missing':
        return <ErrorIcon />;
      default:
        return undefined;
    }
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' => {
    switch (transactionStatus.status) {
      case 'complete':
        return 'success';
      case 'partial':
        return 'warning';
      case 'missing':
        return 'error';
      default:
        return 'default' as any;
    }
  };

  const getTooltipContent = () => {
    const lines = [
      `مبلغ السند: ${transactionStatus.voucherAmount.toFixed(2)} ريال`,
      `عدد المعاملات: ${transactionStatus.transactionsCount} من ${transactionStatus.expectedTransactions}`,
      `الحساب المدين: ${transactionStatus.debitAccount}`,
      `مبلغ المدين: ${transactionStatus.debitAmount.toFixed(2)} ريال`,
      `الحساب الدائن: ${transactionStatus.creditAccount}`,
      `مبلغ الدائن: ${transactionStatus.creditAmount.toFixed(2)} ريال`,
    ];

    if (transactionStatus.issues && transactionStatus.issues.length > 0) {
      lines.push('', 'المشاكل:');
      transactionStatus.issues.forEach(issue => lines.push(`• ${issue}`));
    }

    return lines.join('\n');
  };

  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{getTooltipContent()}</div>} arrow>
      <Chip
        icon={getStatusIcon()}
        label={
          transactionStatus.status === 'complete' ? 'مكتمل' :
          transactionStatus.status === 'partial' ? 'جزئي' : 'مفقود'
        }
        size="small"
        color={getStatusColor()}
      />
    </Tooltip>
  );
};

// المكون الرئيسي
const PaymentsList: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // حالة نافذة عرض السند
  const [viewPaymentId, setViewPaymentId] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // إضافة متغيرات التصفيح
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  
  // حالة المعاملات المالية
  const [voucherTransactions, setVoucherTransactions] = useState<Record<string, VoucherTransactionStatus>>({});
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();

  // جلب بيانات سندات الصرف من الخادم مع pagination
  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // بناء URL مع pagination parameters
      let apiUrl = `/api/finance/payments?page=${page + 1}&limit=${rowsPerPage}`;
      
      // إضافة البحث
      if (searchTerm) {
        apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // إضافة فلتر الحالة
      if (selectedStatus) {
        apiUrl += `&status=${selectedStatus}`;
      }
      
      // إضافة فلتر التاريخ
      if (startDate && endDate) {
        apiUrl += `&startDate=${startDate}&endDate=${endDate}`;
      }
      
      console.log('Fetching payments with URL:', apiUrl);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // Add authentication headers
      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token || '',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`فشل جلب البيانات: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // معالجة الاستجابة
      let paymentsData: Payment[] = [];
      let total = 0;
      
      if (result.success && result.data) {
        paymentsData = result.data;
        total = result.total || result.data.length;
      } else if (Array.isArray(result)) {
        // للتوافق مع الإصدارات القديمة
        paymentsData = result;
        total = result.length;
      }
      
      setPayments(paymentsData);
      setTotalPayments(total);
      
      console.log(`Loaded ${paymentsData.length} payments (total: ${total})`);
    } catch (err: any) {
      console.error('خطأ في جلب سندات الصرف:', err);
      setError('حدث خطأ أثناء جلب البيانات، يرجى المحاولة مرة أخرى');
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };
  
  // جلب البيانات عند تغيير pagination أو filters
  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, selectedStatus, startDate, endDate]);
  
  // إعادة تعيين الصفحة عند تغيير الفلاتر
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedStatus, startDate, endDate]);

  // جلب بيانات المعاملات المالية للسندات
  useEffect(() => {
    const fetchVoucherTransactions = async () => {
      if (payments.length === 0) return;

      // تأخير 600ms قبل جلب البيانات
      const timer = setTimeout(async () => {
        try {
          setTransactionsLoading(true);
          const voucherNumbers = payments.map(p => p.paymentNumber);
          const response = await checkVouchersHaveTransactions(voucherNumbers, 'payment');
          
          if (response.success) {
            setVoucherTransactions(response.results);
          }
        } catch (error) {
          console.error('Error fetching voucher transactions:', error);
        } finally {
          setTransactionsLoading(false);
        }
      }, 600);

      return () => clearTimeout(timer);
    };

    fetchVoucherTransactions();
  }, [payments]);

  // دالة لإعادة تحميل البيانات
  const handleRetry = () => {
    fetchPayments();
  };

  // في Server-side Pagination، البيانات تأتي من Backend مع التصفية والترقيم

  // إعادة تعيين الفلاتر
  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedStatus('');
  };

  // فتح قائمة الإجراءات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, paymentId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedPaymentId(paymentId);
  };

  // إغلاق قائمة الإجراءات
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPaymentId(null);
  };

  // تغيير حالة السند عبر طلب API
  const handleChangeStatus = async (newStatus: string) => {
    if (selectedPaymentId) {
      setLoading(true);
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        const response = await fetch(`/api/finance/payments/${selectedPaymentId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token || ''
          },
          body: JSON.stringify({ status: newStatus }),
        });
        
        if (!response.ok) {
          throw new Error(`فشل تغيير الحالة: ${response.status}`);
        }
        
        // تحديث واجهة المستخدم بعد نجاح العملية
        setPayments(prevPayments =>
          prevPayments.map(payment =>
            payment.id === selectedPaymentId
              ? { ...payment, status: newStatus }
              : payment
          )
        );
      } catch (err) {
        console.error('خطأ في تغيير حالة السند:', err);
        setError('حدث خطأ أثناء تغيير حالة السند');
      } finally {
        setLoading(false);
      }
    }
    handleMenuClose();
  };

  // حذف السند عبر طلب API
  const handleDeletePayment = async () => {
    if (selectedPaymentId) {
      // تحديد ما إذا كان السند مؤكداً لتخصيص رسالة التأكيد
      const selectedPayment = payments.find(p => p.id === selectedPaymentId);
      const isConfirmed = selectedPayment?.status === 'مؤكد';
      
      const confirmMessage = isConfirmed
        ? 'هل أنت متأكد من رغبتك في حذف هذا السند المؤكد؟ سيتم أيضاً حذف القيود المحاسبية المرتبطة به وعكس التأثير على أرصدة الحسابات.'
        : 'هل أنت متأكد من رغبتك في حذف هذا السند؟';
      
      if (window.confirm(confirmMessage)) {
        setLoading(true);
        try {
          // Get token from localStorage
          const token = localStorage.getItem('token');
          
          const response = await fetch(`/api/finance/payments/${selectedPaymentId}`, {
            method: 'DELETE',
            headers: {
              'x-auth-token': token || ''
            }
          });
          
          if (!response.ok) {
            // محاولة استخراج رسالة الخطأ من الاستجابة
            let errorMessage = `فشل حذف السند: ${response.status}`;
            try {
              const errorData = await response.json();
              if (errorData && errorData.message) {
                errorMessage = errorData.message;
              }
            } catch (jsonError) {
              console.error('Failed to parse error response:', jsonError);
            }
            throw new Error(errorMessage);
          }
          
          // تحديث واجهة المستخدم بعد نجاح العملية
          setPayments(prevPayments =>
            prevPayments.filter(payment => payment.id !== selectedPaymentId)
          );
        } catch (err) {
          console.error('خطأ في حذف السند:', err);
          // معالجة الخطأ مع التحقق من نوعه
          let errorMessage = 'حدث خطأ أثناء حذف السند';
          if (err instanceof Error) {
            errorMessage = err.message || errorMessage;
          }
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    }
    handleMenuClose();
  };

  // طباعة السند
  const handlePrintPayment = async (paymentId: string) => {
    try {
      await printPayment(paymentId);
    } catch (error) {
      console.error('خطأ في طباعة السند:', error);
      setError('حدث خطأ أثناء طباعة السند');
    }
  };

  // عرض شريحة الحالة
  const renderStatus = (status: string) => {
    let color;
    let icon;
    
    switch (status) {
      case 'مؤكد':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case 'تحت الموافقة':
        color = 'warning';
        icon = <FilterListIcon fontSize="small" />;
        break;
      case 'مرفوض':
        color = 'error';
        icon = <CancelIcon fontSize="small" />;
        break;
      default:
        color = 'default';
        icon = null;
    }
    
    return (
      <Chip 
        size="small" 
        label={status}
        color={color}
        icon={icon}
        sx={{ 
          fontWeight: 500,
          fontSize: '0.75rem'
        }}
      />
    );
  };
  
  // وظائف التصفيح
  const handleChangePage = (event: unknown, newPage: number) => {
    console.log(`تغيير الصفحة من ${page} إلى ${newPage}`);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log(`تغيير عدد السندات في الصفحة من ${rowsPerPage} إلى ${newRowsPerPage}`);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // في Server-side Pagination، نستخدم payments مباشرة من API
  // لأن الخادم يرسل فقط السندات الخاصة بالصفحة الحالية

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
            background: 'white'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <PaymentIcon sx={{ mr: 1, color: '#30c3b0' }} />
              سندات الصرف
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/finance/payments/new')}
              sx={{
                bgcolor: '#30c3b0',
                '&:hover': { bgcolor: '#25a899' },
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
              }}
            >
              إضافة سند صرف
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Card variant="outlined" sx={{ mb: 3, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                <FilterListIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                البحث والتصفية
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    placeholder="بحث برقم السند، الوصف أو المورد"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    label="حالة السند"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">جميع الحالات</MenuItem>
                    <MenuItem value="مؤكد">مؤكد</MenuItem>
                    <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
                    <MenuItem value="مرفوض">مرفوض</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="من تاريخ"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="إلى تاريخ"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetFilters}
                    sx={{ minWidth: 100 }}
                  >
                    إعادة تعيين
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
            </Box>
          ) : (
            <TableContainer component={Box}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>رقم السند</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell>المورد</TableCell>
                    <TableCell>الحساب المدين</TableCell>
                    <TableCell align="left">المبلغ (ريال)</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>المعاملات</TableCell>
                    <TableCell>المستخدم</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map(payment => (
                    <TableRow key={payment.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{payment.paymentNumber}</TableCell>
                      <TableCell>{new Date(payment.date).toLocaleDateString('ar-SA')}</TableCell>
                      <TableCell>{payment.branchName || 'غير محدد'}</TableCell>
                      <TableCell>{payment.supplier}</TableCell>
                      <TableCell>{payment.debitAccount.name}</TableCell>
                      <TableCell align="left" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.error.main }} />
                          {payment.amount.toLocaleString()}
                        </Box>
                      </TableCell>
                      <TableCell>{renderStatus(payment.status)}</TableCell>
                      <TableCell>
                        <TransactionStatusIndicator
                          transactionStatus={voucherTransactions[payment.paymentNumber] || null}
                          loading={transactionsLoading}
                          voucherNumber={payment.paymentNumber}
                        />
                      </TableCell>
                      <TableCell>{payment.createdBy}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="عرض" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setViewPaymentId(payment.id);
                              setViewDialogOpen(true);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="تعديل" arrow>
                          <IconButton 
                            size="small"
                            component={Link} 
                            to={`/finance/payments/edit/${payment.id}`}
                            sx={{ color: '#30c3b0' }}
                            disabled={payment.status === 'مؤكد'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="طباعة" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handlePrintPayment(payment.id)}
                            sx={{ color: theme.palette.grey[700] }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="المزيد من الخيارات" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleMenuOpen(e, payment.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {error ? (
            <Box sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: 0.8
            }}>
              <CancelIcon sx={{ fontSize: 60, color: theme.palette.error.light, mb: 2 }} />
              <Typography variant="h6" color="error">
                {error}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                }}
                onClick={handleRetry}
                startIcon={<SearchIcon />}
              >
                إعادة محاولة جلب البيانات
              </Button>
            </Box>
          ) : payments.length === 0 && !loading ? (
            <Box sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: 0.8
            }}>
              <PaymentIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                لا توجد سندات صرف مطابقة
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                حاول تغيير معايير البحث أو إضافة سند صرف جديد
              </Typography>
            </Box>
          ) : null }
          
          {/* إضافة شريط التصفيح */}
          {payments.length > 0 && !loading && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalPayments}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="عدد السندات في الصفحة:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} من ${count}`
              }
              sx={{
                mt: 2,
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  '& .MuiTablePagination-spacer': { flex: '0 1 auto' }
                }
              }}
            />
          )}
          
          {/* قائمة الإجراءات */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 2,
              sx: { minWidth: 180, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }
            }}
          >
            {/* عرض جميع الإجراءات بغض النظر عن حالة السند */}
            <MenuItem onClick={() => handleChangeStatus('مؤكد')}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
              </ListItemIcon>
              <ListItemText primary="تأكيد السند" />
            </MenuItem>
            
            <MenuItem onClick={() => handleChangeStatus('مرفوض')}>
              <ListItemIcon>
                <CancelIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText primary="رفض السند" />
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={handleDeletePayment} sx={{ color: theme.palette.error.main }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText primary="حذف السند" />
            </MenuItem>
          </Menu>
        </Paper>
      </Box>
      
      {/* نافذة عرض تفاصيل السند */}
      <PaymentView
        paymentId={viewPaymentId}
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewPaymentId(null);
        }}
      />
    </>
  );
};

export default PaymentsList;